"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import type { MouseEvent as ReactMouseEvent } from "react";
import type { Editor } from "@tiptap/react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import { PlaceholderHighlight } from "./placeholderHighlightExtension";
import { styled } from "@mui/material";
import { Bold, Braces, CircleAlert, Italic, List, Undo2 } from "lucide-react";
import { Alert } from "@/ui";
import { AIInput } from "@components/shared/form/ai";
import ImproveAIButton from "../ImproveAIButton";
import RewriteWithAIPanel from "../RewriteWithAIPanel";
import styles from "../RichAIField/RichAIField.module.css";
import editorStyles from "./RichTextAIField.module.css";
import { isHtmlEmpty, plainTextToHtml, toEditorHtml } from "@lib/helpers/richText";

const cx = (...classNames) => classNames.filter(Boolean).join(" ");

// Tokens the backend substitutes per recipient at send time. Floats and
// Submissions go to contacts (mirrors config.validContactPlaceholders); job
// ad emails go to candidates (mirrors CandidatePlaceholders on the API).
export type PlaceholderScope = "contact" | "candidate";

const PLACEHOLDER_OPTIONS_BY_SCOPE: Record<PlaceholderScope, { field: string; label: string }[]> = {
  contact: [
    { field: "first_name", label: "First name" },
    { field: "last_name", label: "Last name" },
    { field: "company", label: "Company" },
  ],
  candidate: [
    { field: "first_name", label: "First name" },
    { field: "name", label: "Full name" },
  ],
};

const completePlaceholderRegex = (scope: PlaceholderScope) =>
  new RegExp(`^\\{\\{${scope}\\.[a-zA-Z_]+\\}\\}`);

type PlaceholderOption = { field: string; label: string };

type PlaceholderSuggestion = {
  from: number;
  to: number;
  query: string;
  left: number;
  top: number;
};

type RichTextAIFieldProps = {
  id?: string;
  label?: string;
  /** Extra controls rendered at the right of the label row */
  headerActions?: React.ReactNode;
  required?: boolean;
  optional?: boolean;
  minHeight?: number | string;
  value: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  prompt?: string;
  onEnhancementComplete?: (...args: unknown[]) => void;
  disabled?: boolean;
  autoFocus?: boolean;
  onBlur?: (isEmpty: boolean) => void;
  showPlaceholders?: boolean;
  /** Which recipient the {{placeholders}} refer to (default: contact) */
  placeholderScope?: PlaceholderScope;
  alwaysShowToolbar?: boolean;
  error?: string;
};

const getFilteredPlaceholderOptions = (query: string, scope: PlaceholderScope = "contact") => {
  const normalizedQuery = query.toLowerCase().replace(new RegExp(`^${scope}\\.`), "");
  return PLACEHOLDER_OPTIONS_BY_SCOPE[scope].filter(
    ({ field, label }) =>
      field.includes(normalizedQuery) || label.toLowerCase().includes(normalizedQuery)
  );
};

export const findPlaceholderTrigger = (
  textBeforeCaret: string,
  textAfterCaret = "",
  scope: PlaceholderScope = "contact"
) => {
  const match = textBeforeCaret.match(/\{\{([^{}\n]*)$/);
  if (!match) return null;
  // A caret placed inside an existing token sees an apparently unfinished
  // trigger to its left. Join both sides of the caret to distinguish that
  // from a genuinely new `{{` sequence.
  if (completePlaceholderRegex(scope).test(match[0] + textAfterCaret)) return null;
  return { triggerLength: match[0].length, query: match[1].trimStart() };
};

// Plain text ↔ editor HTML helpers live in @lib/helpers/richText so pages that
// only render rich text don't load the editor; re-exported for existing imports.
export { isHtmlEmpty, toEditorHtml };

// A hidden "engine" instance of AIInput — never shown, never typed into.
// It owns the actual /api/enhance-text fetch, streaming, and the
// Enhancement Preview / custom-prompt dialogs (MUI Dialogs portal to
// document.body regardless of this wrapper's own display:none). This
// component just feeds it the editor's current plain text and applies the
// result back into the TipTap document once accepted.
// AIInput is an untyped JS component, so styled() collapses its props to the
// MUI common set. Cast back to a forwardRef component that accepts AIInput's
// own props (hideTrigger, skipPreview, onEnhancement*, …) plus a ref.
const HiddenEngine = styled(AIInput)({
  display: "none",
}) as unknown as React.ForwardRefExoticComponent<
  Record<string, unknown> & React.RefAttributes<unknown>
>;

// Same visual chrome as RichAIField (hover border, focus toolbar, "Rewrite
// with AI" panel), but with a real TipTap editor as the typing surface
// instead of a plain textarea — Bold/Italic/bullet list actually format the
// stored HTML.
export default function RichTextAIField({
  id,
  label,
  headerActions,
  required = false,
  optional = false,
  minHeight,
  value,
  onChange,
  placeholder,
  prompt,
  onEnhancementComplete,
  disabled,
  autoFocus,
  onBlur,
  showPlaceholders = false,
  placeholderScope = "contact",
  alwaysShowToolbar = false,
  error,
}: RichTextAIFieldProps) {
  const [focused, setFocused] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [placeholderMenuOpen, setPlaceholderMenuOpen] = useState(false);
  const [placeholderSuggestion, setPlaceholderSuggestion] =
    useState<PlaceholderSuggestion | null>(null);
  const [activePlaceholderIndex, setActivePlaceholderIndex] = useState(0);
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [plainText, setPlainText] = useState("");
  const [history, setHistory] = useState<string[]>([]);
  // Set when the AI determines the current text has nothing meaningful to
  // enhance (see /api/enhance-text's UNENHANCEABLE marker). While set, the
  // editor is locked (not just visually flagged) — the only way back to
  // editing is dismissing this banner, so the user can't keep typing over
  // an error they haven't acknowledged.
  const [enhancementError, setEnhancementError] = useState("");
  const aiInputRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const fieldWrapperRef = useRef<HTMLDivElement>(null);
  const placeholderMenuWrapperRef = useRef<HTMLDivElement>(null);
  // Anchors the "Rewrite with AI" panel to the toolbar's action group (the
  // Improve button itself) rather than the whole field — otherwise, on a
  // tall multi-paragraph field, the panel would open below all that text
  // instead of right under the button that triggered it.
  const toolbarActionsRef = useRef<HTMLDivElement>(null);
  // The "Rewrite with AI" panel portals to document.body (see
  // RewriteWithAIPanel.js) so it can escape a clipping ancestor (e.g. the
  // email preview's rounded browser frame) — it's no longer a DOM
  // descendant of containerRef, so the outside-click handler below needs
  // this ref too to recognize clicks inside it as "not outside".
  const rewritePanelRef = useRef<HTMLDivElement>(null);
  const placeholderSuggestionRef = useRef<PlaceholderSuggestion | null>(null);
  const activePlaceholderIndexRef = useRef(0);
  const insertSuggestedPlaceholderRef = useRef<(option: PlaceholderOption) => void>(() => {});
  // TipTap's onBlur callback is captured once at editor init, so it'd
  // otherwise close over a stale `panelOpen` — read the current value
  // through a ref instead, kept in sync below.
  const panelOpenRef = useRef(panelOpen);
  useEffect(() => {
    panelOpenRef.current = panelOpen;
  }, [panelOpen]);

  const updatePlaceholderSuggestion = useCallback(
    (currentEditor: Editor) => {
      if (!showPlaceholders || !currentEditor.state.selection.empty) {
        placeholderSuggestionRef.current = null;
        setPlaceholderSuggestion(null);
        return;
      }

      const { $from } = currentEditor.state.selection;
      const textBeforeCaret = $from.parent.textBetween(0, $from.parentOffset, undefined, "\ufffc");
      const textAfterCaret = $from.parent.textBetween(
        $from.parentOffset,
        $from.parent.content.size,
        undefined,
        "\ufffc"
      );
      const trigger = findPlaceholderTrigger(textBeforeCaret, textAfterCaret, placeholderScope);
      if (!trigger) {
        placeholderSuggestionRef.current = null;
        setPlaceholderSuggestion(null);
        return;
      }

      const { query, triggerLength } = trigger;
      const options = getFilteredPlaceholderOptions(query, placeholderScope);
      if (options.length === 0) {
        placeholderSuggestionRef.current = null;
        setPlaceholderSuggestion(null);
        return;
      }

      const fieldRect = fieldWrapperRef.current?.getBoundingClientRect();
      const caretRect = currentEditor.view.coordsAtPos($from.pos);
      const nextSuggestion = {
        from: $from.pos - triggerLength,
        to: $from.pos,
        query,
        left: fieldRect
          ? Math.max(0, Math.min(caretRect.left - fieldRect.left, fieldRect.width - 280))
          : 0,
        top: fieldRect ? caretRect.bottom - fieldRect.top + 6 : 0,
      };
      placeholderSuggestionRef.current = nextSuggestion;
      activePlaceholderIndexRef.current = 0;
      setActivePlaceholderIndex(0);
      setPlaceholderSuggestion(nextSuggestion);
    },
    [showPlaceholders, placeholderScope]
  );

  const editor = useEditor({
    extensions: [
      StarterKit,
      Placeholder.configure({ placeholder: placeholder || "" }),
      PlaceholderHighlight.configure({
        className: styles.placeholderToken,
        scope: placeholderScope,
      }),
    ],
    content: toEditorHtml(value),
    editable: !disabled && !enhancementError,
    immediatelyRender: false,
    onUpdate: ({ editor: currentEditor }) => {
      onChange?.(currentEditor.getHTML());
      setPlainText(currentEditor.getText());
      updatePlaceholderSuggestion(currentEditor);
    },
    onSelectionUpdate: ({ editor: currentEditor }) => updatePlaceholderSuggestion(currentEditor),
    onFocus: () => setFocused(true),
    onBlur: ({ editor: currentEditor }) => {
      setFocused(false);
      // Same guard as below — selecting a panel option blurs the editor
      // as part of that interaction, not the user actually leaving; an
      // error from that very selection can land just after, so clearing
      // unconditionally here would wipe it out before it's ever seen.
      if (!panelOpenRef.current) {
        // Clicking away counts as closing this out — don't leave a
        // dismissed-looking error banner attached to a field the user has
        // already moved on from.
        setEnhancementError("");
        // isEmpty (not the raw HTML) — after deleting all text, TipTap
        // leaves a "<p></p>" behind, which is truthy as a string.
        onBlur?.(currentEditor.isEmpty);
      }
    },
    editorProps: {
      attributes: {
        ...(id ? { id } : {}),
        class: editorStyles.editorWrapper,
        role: "combobox",
        "aria-autocomplete": "list",
        "aria-label": placeholder || "Rich text editor",
      },
      handleKeyDown: (_view, event) => {
        const suggestion = placeholderSuggestionRef.current;
        if (!suggestion) return false;
        const options = getFilteredPlaceholderOptions(suggestion.query);

        if (event.key === "ArrowDown" || event.key === "ArrowUp") {
          event.preventDefault();
          const direction = event.key === "ArrowDown" ? 1 : -1;
          const nextIndex =
            (activePlaceholderIndexRef.current + direction + options.length) % options.length;
          activePlaceholderIndexRef.current = nextIndex;
          setActivePlaceholderIndex(nextIndex);
          return true;
        }
        if (event.key === "Enter" || event.key === "Tab") {
          event.preventDefault();
          insertSuggestedPlaceholderRef.current(options[activePlaceholderIndexRef.current]);
          return true;
        }
        if (event.key === "Escape") {
          event.preventDefault();
          placeholderSuggestionRef.current = null;
          setPlaceholderSuggestion(null);
          return true;
        }
        return false;
      },
    },
  });

  useEffect(() => {
    const editorElement = editor?.view.dom;
    if (!editorElement) return;
    editorElement.setAttribute("aria-expanded", String(Boolean(placeholderSuggestion)));
    if (placeholderSuggestion) {
      editorElement.setAttribute("aria-controls", "placeholder-suggestions");
      editorElement.setAttribute("aria-activedescendant", `placeholder-option-${activePlaceholderIndex}`);
    } else {
      editorElement.removeAttribute("aria-controls");
      editorElement.removeAttribute("aria-activedescendant");
    }
  }, [editor, placeholderSuggestion, activePlaceholderIndex]);

  // Keep the editor in sync with external value changes (e.g. draft load,
  // or the hidden engine applying an accepted AI rewrite) — adjust state
  // during render rather than in an effect, since this only needs to run
  // once per divergence, not after every commit.
  const [prevValue, setPrevValue] = useState(value);
  if (editor && value !== prevValue) {
    setPrevValue(value);
    const current = editor.getHTML();
    const next = toEditorHtml(value);
    if (next !== current) {
      editor.commands.setContent(next, { emitUpdate: false });
    }
    if (editor.getText() !== plainText) {
      setPlainText(editor.getText());
    }
  }

  useEffect(() => {
    if (editor) editor.setEditable(!disabled && !enhancementError);
  }, [editor, disabled, enhancementError]);

  const handleDismissError = () => {
    setEnhancementError("");
    editor?.commands.focus();
  };

  // Closing the "Rewrite with AI" panel — via its own close button,
  // clicking away, or toggling it shut — counts the same as clicking away
  // from the field itself: don't leave a stale error banner attached once
  // the user has moved on from it.
  const closePanel = () => {
    setPanelOpen(false);
    setEnhancementError("");
  };

  // `editor` (immediatelyRender: false) isn't available on the first
  // render — waits for it to become ready, then focuses once. Callers
  // only pass autoFocus on components that mount fresh when revealed
  // (e.g. swapping in for an empty-state prompt), so "once per mount" is
  // the right lifetime for this, not "every time autoFocus is truthy".
  useEffect(() => {
    if (autoFocus && editor) editor.commands.focus();
  }, [editor, autoFocus]);

  const showToolbar =
    alwaysShowToolbar || focused || panelOpen || placeholderMenuOpen || isEnhancing;

  useEffect(() => {
    if (!panelOpen && !placeholderMenuOpen) return undefined;

    const handleOutside = (event: MouseEvent | FocusEvent) => {
      const target = event.target as Node;
      const insideContainer = containerRef.current?.contains(target);
      const insideRewritePanel = rewritePanelRef.current?.contains(target);
      const insidePlaceholderMenu =
        placeholderMenuWrapperRef.current?.contains(target);

      if (placeholderMenuOpen && !insidePlaceholderMenu) {
        setPlaceholderMenuOpen(false);
      }

      if (panelOpen && !insideContainer && !insideRewritePanel) {
        closePanel();
      }
    };

    // mousedown covers clicking away; focusin covers tabbing away (blur
    // doesn't bubble, so document-level focusin is the reliable way to
    // catch keyboard-driven focus moving to a different section).
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("focusin", handleOutside);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("focusin", handleOutside);
    };
  }, [panelOpen, placeholderMenuOpen]);

  const handleEnhancedText = useCallback(
    (enhancedText: string) => {
      const html = plainTextToHtml(enhancedText);
      editor?.commands.setContent(html);
      setPlainText(editor?.getText() || "");
      onChange?.(html);
    },
    [editor, onChange]
  );

  const handleOptionSelect = (id: string) => {
    setHistory((prev) => [...prev, value]);
    aiInputRef.current?.enhanceOption(id);
  };

  const handleCustomSubmit = (customPrompt: string) => {
    setHistory((prev) => [...prev, value]);
    aiInputRef.current?.enhanceCustom(customPrompt);
  };

  const handleUndo = () => {
    if (history.length === 0) return;
    onChange?.(history[history.length - 1]);
    setHistory((prev) => prev.slice(0, -1));
  };

  const handleInsertPlaceholder = (field: PlaceholderOption["field"]) => {
    editor?.chain().focus().insertContent(`{{${placeholderScope}.${field}}}`).run();
    setPlaceholderMenuOpen(false);
  };

  const handleSuggestedPlaceholder = useCallback(
    (option: PlaceholderOption) => {
      const suggestion = placeholderSuggestionRef.current;
      if (!editor || !suggestion) return;
      editor
        .chain()
        .focus()
        .deleteRange({ from: suggestion.from, to: suggestion.to })
        .insertContent(`{{${placeholderScope}.${option.field}}}`)
        .run();
      placeholderSuggestionRef.current = null;
      setPlaceholderSuggestion(null);
    },
    [editor, placeholderScope]
  );
  useEffect(() => {
    insertSuggestedPlaceholderRef.current = handleSuggestedPlaceholder;
  }, [handleSuggestedPlaceholder]);

  const suggestionOptions = placeholderSuggestion
    ? getFilteredPlaceholderOptions(placeholderSuggestion.query)
    : [];

  return (
    <div className={editorStyles.root}>
    {(label || headerActions) && (
      <div className={editorStyles.labelRow}>
        {label && (
          <label className={editorStyles.label} htmlFor={id}>
            {label}
            {required && <span className={editorStyles.required}>*</span>}
            {optional && !required && <span className={editorStyles.optional}>(Optional)</span>}
          </label>
        )}
        {headerActions}
      </div>
    )}
    <div
      ref={containerRef}
      className={cx(styles.container, showToolbar && styles.focused, error && styles.error)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {showToolbar && (
        <div className={styles.toolbar}>
          <button
            type="button"
            className={cx(
              styles.formatButton,
              styles.interactive,
              editor?.isActive("bold") && styles.formatButtonActive
            )}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => editor?.chain().focus().toggleBold().run()}
          >
            <Bold size={16} />
          </button>
          <button
            type="button"
            className={cx(
              styles.formatButton,
              styles.interactive,
              editor?.isActive("italic") && styles.formatButtonActive
            )}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => editor?.chain().focus().toggleItalic().run()}
          >
            <Italic size={16} />
          </button>
          <button
            type="button"
            className={cx(
              styles.formatButton,
              styles.interactive,
              editor?.isActive("bulletList") && styles.formatButtonActive
            )}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => editor?.chain().focus().toggleBulletList().run()}
          >
            <List size={16} />
          </button>
          {showPlaceholders && (
            <div
              ref={placeholderMenuWrapperRef}
              className={styles.placeholderMenuWrapper}
            >
              <button
                type="button"
                className={cx(
                  styles.formatButton,
                  styles.interactive,
                  placeholderMenuOpen && styles.formatButtonActive
                )}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => setPlaceholderMenuOpen((open) => !open)}
                title="Insert placeholder"
                aria-label="Insert placeholder"
                aria-haspopup="menu"
                aria-expanded={placeholderMenuOpen}
              >
                <Braces size={16} />
              </button>
              {placeholderMenuOpen && (
                <div className={styles.placeholderMenu} role="menu">
                  {PLACEHOLDER_OPTIONS_BY_SCOPE[placeholderScope].map((option) => (
                    <button
                      key={option.field}
                      type="button"
                      role="menuitem"
                      className={styles.placeholderMenuItem}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => handleInsertPlaceholder(option.field)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          <span className={styles.divider} />
          <div className={styles.aiActions} ref={toolbarActionsRef}>
            {history.length > 0 && (
              <button
                type="button"
                className={cx(styles.formatButton, styles.interactive)}
                onMouseDown={(event) => event.preventDefault()}
                onClick={handleUndo}
                title="Undo last AI change"
              >
                <Undo2 size={16} />
              </button>
            )}
            <ImproveAIButton
              active={panelOpen}
              onClick={() => (panelOpen ? closePanel() : setPanelOpen(true))}
            />
          </div>
        </div>
      )}

      <div
        ref={fieldWrapperRef}
        style={minHeight ? { minHeight } : undefined}
        className={cx(
          styles.fieldWrapper,
          showToolbar && styles.focused,
          editorStyles.fieldPadding,
          !showToolbar && hovered && editorStyles.fieldPaddingHover,
          showToolbar && editorStyles.fieldPaddingFocused
        )}
      >
        <EditorContent editor={editor} />
        {placeholderSuggestion && (
          <div
            id="placeholder-suggestions"
            className={styles.inlinePlaceholderMenu}
            role="listbox"
            aria-label="Placeholder suggestions"
            style={{ left: placeholderSuggestion.left, top: placeholderSuggestion.top }}
          >
            {suggestionOptions.map((option, index) => (
              <button
                key={option.field}
                id={`placeholder-option-${index}`}
                type="button"
                role="option"
                aria-selected={index === activePlaceholderIndex}
                className={cx(
                  styles.placeholderMenuItem,
                  index === activePlaceholderIndex && styles.placeholderMenuItemActive
                )}
                onMouseDown={(event: ReactMouseEvent<HTMLButtonElement>) => {
                  event.preventDefault();
                  handleSuggestedPlaceholder(option);
                }}
                onMouseEnter={() => {
                  activePlaceholderIndexRef.current = index;
                  setActivePlaceholderIndex(index);
                }}
              >
                <span>{option.label}</span>
                <span className={styles.placeholderMenuToken}>{`{{${placeholderScope}.${option.field}}}`}</span>
              </button>
            ))}
          </div>
        )}
        {isEnhancing && (
          <div className={styles.skeletonOverlay} aria-hidden="true">
            {["100%", "92%", "96%", "60%", "100%", "88%", "94%", "40%"].map((width, index) => (
              <span
                key={index}
                className={styles.skeletonLine}
                style={{ width }}
              />
            ))}
          </div>
        )}
      </div>

      {enhancementError && (
        <div className={styles.errorBannerWrapper}>
          <Alert variant="error" dismissible onDismiss={handleDismissError}>
            {enhancementError}
          </Alert>
        </div>
      )}

      <HiddenEngine
        ref={aiInputRef}
        hideTrigger
        skipPreview
        showErrorSnackbar={false}
        value={plainText}
        onChange={handleEnhancedText}
        onEnhancementStart={() => {
          setIsEnhancing(true);
          setEnhancementError("");
        }}
        onEnhancementComplete={(...args) => {
          setIsEnhancing(false);
          onEnhancementComplete?.(...args);
        }}
        onEnhancementError={(err) => {
          setIsEnhancing(false);
          setEnhancementError(err?.message || "Enhancement failed. Please try again.");
        }}
        prompt={prompt}
        variant="textarea"
      />

      {panelOpen && (
        <RewriteWithAIPanel
          onClose={closePanel}
          onOptionSelect={handleOptionSelect}
          onCustomSubmit={handleCustomSubmit}
          isEnhancing={isEnhancing}
          anchorRef={toolbarActionsRef}
          panelRef={rewritePanelRef}
        />
      )}
    </div>
      {error && (
        <div className={styles.errorText}>
          <CircleAlert size={14} className={styles.errorIcon} aria-hidden="true" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}
